import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { getKeycloak } from "../auth/keycloak";
import { keycloakLogout } from "../auth/logout";
import {
  getSessionIdleConfig,
  setSessionIdleHandlers,
  startSessionIdleWatch,
  stopSessionIdleWatch,
  unlockSessionIdle,
} from "../auth/sessionIdle";
import {
  refreshSessionNow,
  startTokenRefreshInterval,
  stopTokenRefreshInterval,
} from "../auth/tokenRefresh";
import { feedbackStyles } from "./AppFeedback";

function formatCountdown(totalSeconds) {
  const safe = Math.max(0, totalSeconds);
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

export default function SessionIdleGuard({ children }) {
  const [open, setOpen] = useState(false);
  const [extending, setExtending] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const config = getSessionIdleConfig();
  const graceSeconds = Math.ceil(config.graceMs / 1000);

  const handleExpire = useCallback(async () => {
    setOpen(false);
    stopTokenRefreshInterval();
    stopSessionIdleWatch();
    localStorage.removeItem("authToken");
    try {
      await keycloakLogout(window.location.origin);
    } catch {
      window.location.href = window.location.origin;
    }
  }, []);

  const handleExtend = useCallback(async () => {
    setExtending(true);
    try {
      await refreshSessionNow();
      unlockSessionIdle();
      startTokenRefreshInterval();
      setOpen(false);
    } catch (err) {
      console.error("Failed to extend session", err);
      await handleExpire();
    } finally {
      setExtending(false);
    }
  }, [handleExpire]);

  useEffect(() => {
    const kc = getKeycloak();
    if (!kc?.authenticated) {
      return undefined;
    }

    setSessionIdleHandlers({
      onWarning: () => {
        stopTokenRefreshInterval();
        setOpen(true);
      },
      onWarningClear: () => setOpen(false),
      onExpire: () => {
        void handleExpire();
      },
    });

    startSessionIdleWatch();
    startTokenRefreshInterval();

    return () => {
      stopSessionIdleWatch();
      stopTokenRefreshInterval();
    };
  }, [handleExpire]);

  useEffect(() => {
    if (!open) {
      setSecondsLeft(0);
      return undefined;
    }

    setSecondsLeft(graceSeconds);
    const intervalId = window.setInterval(() => {
      setSecondsLeft((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(intervalId);
  }, [open, graceSeconds]);

  const extendLabel = extending
    ? "Extending…"
    : `Extend session (${formatCountdown(secondsLeft)})`;

  return (
    <>
      {children}
      <Dialog
        open={open}
        disableEscapeKeyDown
        onClose={(_, reason) => {
          if (reason === "backdropClick") return;
        }}
        PaperProps={{ sx: feedbackStyles.dialog.paperSx }}
      >
        <DialogTitle sx={feedbackStyles.dialog.titleSx}>Session timeout</DialogTitle>
        <DialogContent sx={feedbackStyles.dialog.contentSx}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            You have been inactive for {config.idleMinutes} minutes. Do you want to extend your
            session?
          </Typography>
          <Typography variant="caption" color="text.secondary">
            You will be signed out automatically in {formatCountdown(secondsLeft)} unless you
            extend your session.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => void handleExpire()}
            disabled={extending}
            sx={feedbackStyles.dialog.cancelBtnSx}
          >
            Sign out
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleExtend()}
            disabled={extending || secondsLeft <= 0}
            sx={feedbackStyles.dialog.confirmBtnSx}
          >
            {extendLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
